import { RouteModel } from '@polaris/core/models'
import { infiniteQueryOptions } from '@tanstack/react-query'
import { getQueryClient } from '@/src/configs/tanstack-query'
import { type GetHistoryRequest, getHistory } from '@/src/services/history'

const HISTORY_PER_PAGE_DEFAULT = 10

interface HistoryQueryProps
  extends Omit<GetHistoryRequest, 'page' | 'per-page'> {
  /** Default: 10 */
  perPage?: number
}

const HISTORY_QUERY_KEY = 'history'

export const getHistoryQuery = ({
  perPage = HISTORY_PER_PAGE_DEFAULT,
  ...request
}: HistoryQueryProps) => {
  // Explicitly access params from given request to avoid leakage of data from request object into query key
  // possibly preventing redundant queries. Page is managed by the infinite query options.
  const params = {
    wallets: request.wallets,
    'per-page': perPage.toString(),
  }

  return infiniteQueryOptions({
    queryKey: [HISTORY_QUERY_KEY, 'infinite', params],
    queryFn: ({ pageParam }) =>
      getHistory({
        ...params,
        page: pageParam.toString(),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage?.routes?.length === perPage) {
        return lastPage.page + 1
      }
      return undefined
    },
    getPreviousPageParam: (firstPage) => {
      const page = firstPage.page - 1
      if (page <= 0) {
        return undefined
      }
      return undefined
    },
    refetchInterval: (query) => {
      const routes = query.state.data?.pages.flatMap((page) => page.routes)
      const isSomeRouteRunning = routes?.some(
        (route) => new RouteModel(route).isRunning,
      )

      // Dynamic gcTime: if routes are running, keep cache indefinitely (Infinity)
      // Otherwise, use default 5 minutes to allow normal garbage collection
      const queryCache = getQueryClient()?.getQueryCache()
      const existingQuery = queryCache?.find({
        queryKey: [HISTORY_QUERY_KEY, 'infinite', params],
      })

      if (existingQuery) {
        // Update gcTime dynamically based on whether routes are running
        existingQuery.gcTime = isSomeRouteRunning ? Infinity : 5 * 60 * 1000
      }

      return isSomeRouteRunning ? 5_000 : false
    },
    enabled: request.wallets.length > 0,
  })
}

/** Invalidates all history queries in query client. */
export const invalidateHistory = () => {
  getQueryClient()?.invalidateQueries({
    queryKey: [HISTORY_QUERY_KEY],
    refetchType: 'all',
  })
}
